/** Response Board — multi-dispatch queue (Page181 Soft≠Live). Soft≠Live. */
import { useState, useMemo, useEffect, _useCallback, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { html, M, F, Ico, KV, _Card, _Field, _Input, Alert, _Ticker,
         SOFT, FADE, go, _stay, stop, _useEsc, _hashSearchParams, _handoffKnownParamsOf} from "./ui.js";
import { _api, path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useEventsLive } from "./stream-live.js";
import {
  dispatchRowsOf,
  _candidateRowsOf,
  _personnelRosterOf,
  _dispatchRulesOf,
  _candidatePlanImportBody,
  _draftPlanFromLiveRows,
  softResponseErrorKind,
  softResponseErrorOf,
} from "./response-live.js";
import { boardHeadingOf, operatorSay } from "./operator-say.js";
export { softResponseErrorOf };

function safeBoardPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__response_board_status") return "/response/board/status";
    if (op === "get__responder-dispatches") {
      const lim = (opts && opts.query && opts.query.limit) || 64;
      return `/responder-dispatches?limit=${encodeURIComponent(lim)}`;
    }
    if (op === "get__responder-dispatches_mine") {
      const lim = (opts && opts.query && opts.query.limit) || 64;
      return `/responder-dispatches/mine?limit=${encodeURIComponent(lim)}`;
    }
    if (op === "post__responder-dispatches_p_transition") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/responder-dispatches/${encodeURIComponent(id)}/transition`;
    }
    if (op === "post__responder-dispatches_mine_p_transition") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/responder-dispatches/mine/${encodeURIComponent(id)}/transition`;
    }
    return "";
  }
}

/** Pure: seat chips from Live board posture — fail-closed. */
export function responseBoardSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const max = Number(lim.list_max);
  const def = Number(lim.list_default);
  return {
    teamListAllowed: p.team_list_allowed === true,
    mineListAllowed: p.mine_list_allowed === true,
    teamListRequiresOwnerAdmin: p.team_list_requires_owner_admin === true,
    ownerAdminQuery: String(p.owner_admin_query || "owner_admin"),
    teamTransitionAllowed: p.team_transition_allowed === true,
    mineTransitionAllowed: p.mine_transition_allowed === true,
    gpsObservationGate: p.gps_observation_gate === true,
    enRouteRequiresFreshGps: p.en_route_requires_fresh_gps === true,
    onSceneRequiresFreshGps: p.on_scene_requires_fresh_gps === true,
    gpsClaimGreen: p.gps_claim_green === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    listLimit:
      Number.isFinite(def) && def > 0
        ? Math.min(def, Number.isFinite(max) && max > 0 ? max : 256)
        : 64,
  };
}

export const COLS = ["pending","accepted","en_route","on_scene","resolved"];
export const BOARD_META = {
  pending: ["Pending", "Waiting for a responder"],
  accepted: ["Accepted", "Responder committed"],
  en_route: ["En route", "Moving to the incident"],
  on_scene: ["On scene", "Response is active"],
  resolved: ["Resolved", "Closed with outcome"],
};

export function BoardPane({ toast }){
  const posture = useApi(safeBoardPath("get__response_board_status"));
  const post = posture.data || {};
  const seat = responseBoardSeatOf(posture.loading ? null : post);
  const busMode = seat.teamListAllowed ? "team" : seat.mineListAllowed ? "mine" : "none";
  const listPath =
    busMode === "team"
      ? safeBoardPath("get__responder-dispatches", { query: { limit: seat.listLimit } })
      : busMode === "mine"
        ? safeBoardPath("get__responder-dispatches_mine", { query: { limit: seat.listLimit } })
        : "";
  const live = useApi(listPath);
  const list = useMemo(() => dispatchRowsOf(live.data), [live.data]);
  const [open,setOpen] = useState(null);
  const [only,setOnly] = useState(null);
  const act = useAction();
  const canWrite =
    (busMode === "team" && seat.teamTransitionAllowed)
    || (busMode === "mine" && seat.mineTransitionAllowed);
  /* Same Live bus as Roster: SSE events trigger dispatch list re-read (poll remains). */
  const boardEvents = useEventsLive(15000);
  const boardDispatchSeen = useRef("");
  useEffect(() => {
    const events = boardEvents.data && Array.isArray(boardEvents.data.items)
      ? boardEvents.data.items
      : [];
    const hit = events.find((ev) =>
      String((ev && ev.event_type) || "").startsWith("responder.dispatch."),
    );
    const eid = hit ? String(hit.id || hit.event_id || hit.ts_epoch_ms || "") : "";
    if (!eid || eid === boardDispatchSeen.current) return;
    boardDispatchSeen.current = eid;
    live.reload && live.reload();
    posture.reload && posture.reload();
  }, [boardEvents.data, live.reload, posture.reload]);

  const move = async (id, to) => {
    if (!canWrite) {
      toast("You cannot change dispatch status on this seat", "warn");
      return;
    }
    const op = busMode === "mine"
      ? "post__responder-dispatches_mine_p_transition"
      : "post__responder-dispatches_p_transition";
    const out = await act.run(
      safeBoardPath(op, { pathParams: [id] }),
      { method: "POST", body: { status: to } },
    );
    if (out) {
      const green = out.gps_claim_green === true || out.gps_observed === true;
      const stage = String(out.outcome_stage || "status_written");
      const needsGps =
        (to === "en_route" && seat.enRouteRequiresFreshGps)
        || (to === "on_scene" && seat.onSceneRequiresFreshGps);
      toast(
        green
          ? ("Dispatch moved to " + operatorSay(to, to.replace("_", " ")))
          : ("Dispatch moved to " + operatorSay(to, to.replace("_", " "))
            + " · " + operatorSay(stage, "Status saved")
            + (seat.gpsObservationGate && needsGps && !green
              // GPS not observed (R3) — honesty seal stays in source; operator toast is a sentence.
              ? " · GPS not observed"
              : "")),
        green ? "go" : "warn",
      );
      live.reload && live.reload();
    } else {
      toast(softResponseErrorOf(act.error || "Transition refused"), "bad");
    }
  };
  const reRead = () => {
    posture.reload && posture.reload();
    live.reload && live.reload();
    toast("Board refreshed", "go");
  };
  const count = c => list.filter(x=>x.status===c).length;
  const filter = c => {
    const to = only===c ? null : c;
    setOnly(to);
    toast(to ? "Showing " + operatorSay(to, to.replace("_"," ")) + " · " + count(c) : "Showing all states");
  };
  const d = list.find(x=>x.id===open);
  const nextOf = s => COLS[COLS.indexOf(s)+1];
  const lastEnv = act.envelopeOf ? act.envelopeOf() : null;
  const scopeSay = busMode === "team"
    ? "Team queue for managers — advance each card as the crew moves."
    : busMode === "mine"
      ? "Your personal queue only — other teams stay hidden."
      : "No dispatch queue for this account yet.";
  const activeCount = list.filter((x) => !["resolved", "cancelled"].includes(x.status)).length;
  const attentionCount = list.filter((x) => Number(x.risk) >= 60).length;

  return html`
    <${F} data-cmc-residual="page181-board" data-cmc-board-ux="rsp-board-v2">
      <header className="board-v2__head">
        <div>
          <p className="eyebrow">Multi-incident dispatch</p>
          <h2>Board</h2>
          <p>${scopeSay} Each card is one dispatch, not the incident record.</p>
        </div>
        <div className="board-v2__pulse" aria-label="Dispatch queue summary">
          <span><small>Active</small><b>${activeCount}</b></span>
          <span data-tone=${attentionCount ? "warn" : "go"}><small>High risk</small><b>${attentionCount}</b></span>
          <span><small>Queue</small><b>${busMode === "team" ? "team" : busMode === "mine" ? "mine" : "sealed"}</b></span>
          <span title="Events transport for board refresh">
            <small>Live</small><b>${boardEvents.transport === "sse" ? "SSE" : "POLL"}</b>
          </span>
        </div>
      </header>
      ${seat.teamListRequiresOwnerAdmin
        ? html`<${Alert} tone="warn" title="Tenant scope required"
            say=${softResponseErrorOf(seat.detail || "board_team_requires_owner_admin")}/>`
        : null}
      ${!posture.loading && !seat.enabled
        ? html`<${Alert} tone="warn" title="Board seat closed" say=${softResponseErrorOf(seat.detail || "Board closed")}/>`
        : null}
      ${live.error ? html`<${Alert} tone="warn"
        title=${(softResponseErrorKind(live.error) === "site" || softResponseErrorKind(live.error) === "sa_site")
          ? "Site scope required"
          : "Could not refresh dispatches"}
        say=${softResponseErrorOf(live.error)}/>` : null}
      <div className="btn-row resp-desk__acts">
        <p className="hint" style=${{ margin:0 }}>
          Select a stage to focus it. Select it again to restore the full route.
        </p>
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${!seat.reReadAllowed && !posture.loading}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${13}/> Refresh</button>
        ${only && html`<button type="button" className="btn btn--ghost btn--sm" onClick=${()=>filter(only)}>Show all</button>`}
      </div>

      <div className="board-v2__route" aria-label="Dispatch progress">
        ${COLS.map((c, i)=> html`
          <button type="button" key=${c} data-on=${only===c?"1":"0"}
            data-muted=${only && only!==c?"1":"0"} onClick=${()=>filter(c)}>
            <i>${i + 1}</i><span><b>${BOARD_META[c][0]}</b><small>${BOARD_META[c][1]}</small></span>
            <em>${count(c)}</em>
          </button>`)}
      </div>

      <div className="cols board-v2__lanes mt">
        ${COLS.map((c, laneIndex)=> html`
          <section key=${c} className="col2 board-v2__lane" data-stage=${c}
            data-muted=${only && only!==c?"1":"0"}>
            <button className="col2__h board-v2__lane-head" data-on=${only===c?"1":"0"} onClick=${()=>filter(c)}>
              <span><b>${BOARD_META[c][0]}</b><small>${BOARD_META[c][1]}</small></span>
              <em>${count(c)}</em>
            </button>
            <div className="col2__b">
              ${busMode === "none"
                ? html`<div className="col2__e">sealed</div>`
                : only && only!==c
                ? html`<div className="col2__e">hidden</div>`
                : list.filter(x=>x.status===c).map((x, cardIndex)=> html`
                <${M.button} key=${x.id} layout layoutId=${"dispatch-" + x.id}
                    className="dcard glass board-v2__card"
                    initial=${{ opacity:0, y:10 }} animate=${{ opacity:1, y:0 }}
                    transition=${{ ...SOFT, delay:Math.min((laneIndex + cardIndex) * .025, .18) }}
                    onClick=${()=>setOpen(x.id)} aria-label=${"Open dispatch"}>
                  <div className="dcard__in">
                    <div className="dcard__h"><b>${boardHeadingOf(x.devLabel)}</b><i className="risk" data-hi=${x.risk>=60?"1":"0"}>Risk ${x.risk}</i></div>
                    <span className="dcard__w"><${Ico} d="user" w=${12}/>${x.who}</span>
                    <em>${operatorSay(x.reason, "Needs a look")}</em>
                    <span className="dcard__f">${x.opened || ""}</span>
                  </div>
                <//>`)}
              ${busMode !== "none" && (!only || only===c) && count(c)===0 && html`<div className="col2__e">${live.loading || posture.loading ? "…" : "—"}</div>`}
            </div>
          </section>`)}
      </div>

      <${AnimatePresence}>
        ${d && html`
          <${M.div} key="d" className="scrim board-v2__drawer-scrim" initial=${{ opacity:0 }} animate=${{ opacity:1 }}
              exit=${{ opacity:0 }} transition=${FADE} onClick=${()=>setOpen(null)}>
            <${M.div} layoutId=${"dispatch-" + d.id} className="sheetlet glass board-v2__drawer"
                initial=${{ x:80, opacity:0 }} animate=${{ x:0, opacity:1 }}
                exit=${{ x:40, opacity:0 }}
                transition=${{ type:"spring", stiffness:370, damping:38, mass:.85 }} onClick=${stop}>
              <div className="sheetlet__in">
                <div className="sec__h">
                  <div><p className="eyebrow">Dispatch detail</p>
                    <h3 style=${{ margin:0, fontSize:"var(--t-lead)", fontWeight:600 }}>${boardHeadingOf(d.devLabel)}</h3></div>
                  <button className="x" onClick=${()=>setOpen(null)}><${Ico} d="close" w=${13}/></button>
                </div>
                <p className="note">A dispatch coordinates a responder and device. Open its case in Mission Room for incident command, evidence, and review.</p>
                <${KV} k="Device" v=${boardHeadingOf(d.devLabel || d.dev)}/>
                <${KV} k="Sent to" v=${d.who}/>
                <${KV} k="Status" v=${operatorSay(d.status, d.status)} tone=${d.status==="resolved"?"good":d.status==="cancelled"?"":"warn"}/>
                <${KV} k="Risk score" v=${d.risk}/>
                <${KV} k="Why it was raised" v=${operatorSay(d.reason, "Needs a look")}/>
                <${KV} k="Distance" v=${"not recorded on this job"}/>
                <${KV} k="Reference" v=${d.correlation}/>
                <${KV} k="Queue" v=${busMode === "team" ? "team" : busMode === "mine" ? "mine" : "sealed"}/>
                ${lastEnv ? html`<${KV}
                  k="Last action"
                  v=${envelopeSummary(lastEnv) || "—"}
                  tone=${lastEnv.ok ? "hi" : "warn"}/>` : null}
                ${d.resolution && html`<${KV} k="Resolution" v=${d.resolution}/>`}
                <div className="btn-row" style=${{ marginTop:12 }}>
                  <button className="btn btn--ghost btn--sm" disabled=${!d.incident}
                    onClick=${()=>go("/response/cases?incident=" + encodeURIComponent(d.incident))}>
                    Open Mission Room</button>
                  ${canWrite && nextOf(d.status) && html`<button className="btn btn--go btn--sm" disabled=${act.busy}
                    onClick=${()=>{ move(d.id, nextOf(d.status)); setOpen(null); }}>
                    Move to ${operatorSay(nextOf(d.status), nextOf(d.status).replace("_"," "))}</button>`}
                  ${canWrite && d.status!=="resolved" && d.status!=="cancelled" && html`
                    <button className="btn btn--danger btn--sm" disabled=${act.busy}
                      onClick=${()=>{ move(d.id,"cancelled"); setOpen(null); }}>Cancel</button>`}
                  ${!canWrite && html`<p className="note">You cannot change status on this seat.</p>`}
                </div>
              </div>
            <//>
          <//>`}
      <//>
    <//>`;
}
